import React, { forwardRef, Ref, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-style-guide';
import {
  Thumbnail2d,
  ThumbnailTypes,
  ThumbnailFormat,
  ThumbnailGameIconSize,
  ThumbnailAvatarHeadshotSize
} from 'roblox-thumbnails';
import { PlayButton, Presence } from 'Roblox';
import { FeatureGamePage } from '../constants/translationConstants';
import { queryParams } from '../constants/browserConstants';
import { common } from '../constants/configConstants';
import { buildGameDetailUrl, getSession } from '../utils/browserUtils';
import useFriendsPresence from '../hooks/useFriendsPresence';
import { TGameTileProps, GameTileStats, ShimmedPlayerInteractionModal } from './GameTileUtils';
import { TGetGameDetails } from '../types/bedev1Types';
import bedev1Services from '../services/bedev1Services';
import '../../../../css/common/_gameTiles.scss';

const { PlayabilityStatuses } = PlayButton;
const LoadingPlayButton = () => <div className='shimmer play-button game-card-thumb-container' />;
const PurchasePlayButton = () => <span className='icon-common-play' />;
const ErrorPlayButton = (status: keyof typeof PlayabilityStatuses) => {
  switch (status) {
    case PlayabilityStatuses.UniverseRootPlaceIsPrivate:
      return (
        <div className='btn-growth-lg play-button'>
          <span className='icon-status-private' />
        </div>
      );
    default:
      return (
        <div className='btn-growth-lg play-button'>
          <span className='icon-status-unavailable' />
        </div>
      );
  }
};

const gameIconSize = 32;
const gameIconOverlap = 10;

export const FeaturedGridTile = forwardRef(
  (
    { id, onTileClick, gameData, translate }: TGameTileProps,
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    const [game, setGame] = useState<TGetGameDetails | undefined>();
    const [isModalShown, setModalShown] = useState(false);
    const friendsData = useFriendsPresence();
    const session = getSession(queryParams.gameSearchQuerySession);
    const friendsDataInGame = useMemo(
      () =>
        friendsData.filter(
          friend =>
            friend.presence?.universeId === gameData.universeId &&
            friend.presence?.userPresenceType === Presence.PresenceTypes.InGame
        ),
      [friendsData, gameData.universeId]
    );

    const cardDescription = useMemo(() => {
      switch (friendsDataInGame.length) {
        case 0:
          return <div className='game-card-description-info font-body'>{game?.description}</div>;
        default:
          return (
            <div className='game-card-friend-info game-card-info'>
              <div
                className='info-avatar'
                style={{
                  width: `${
                    (friendsDataInGame.slice(0, common.numberOfInGameIcons).length - 1) *
                      (gameIconSize - gameIconOverlap) +
                    gameIconSize
                  }px`
                }}>
                {friendsDataInGame.slice(0, common.numberOfInGameIcons).map(friend => (
                  <div
                    role='button'
                    tabIndex={0}
                    key={friend.displayName}
                    onClick={e => {
                      e.preventDefault();
                      setModalShown(true);
                    }}
                    onKeyDown={() => {
                      setModalShown(true);
                    }}>
                    <Thumbnail2d
                      type={ThumbnailTypes.avatarHeadshot}
                      size={ThumbnailAvatarHeadshotSize.size48}
                      targetId={friend.id}
                      containerClass='avatar avatar-headshot avatar-headshot-xs'
                      imgClassName='avatar-card-image'
                      format={ThumbnailFormat.png}
                      altName={friend.displayName}
                    />
                  </div>
                ))}
              </div>
              <span className='info-label text-overflow'>
                {friendsDataInGame.length > common.numberOfInGameNames
                  ? translate(FeatureGamePage.LabelPlayingOnePlusUsersWithComma, {
                      username: friendsDataInGame[0].displayName,
                      count: friendsDataInGame.length - common.numberOfInGameNames
                    })
                  : translate(FeatureGamePage.LabelPlayingOneUser, {
                      user: friendsDataInGame[0].displayName
                    })}
              </span>
              {game && (
                <ShimmedPlayerInteractionModal
                  friendsDataInGame={friendsDataInGame}
                  game={game}
                  show={isModalShown}
                  onHide={() => {
                    setModalShown(false);
                  }}
                />
              )}
            </div>
          );
      }
    }, [friendsDataInGame]);

    useEffect(() => {
      const fetchGameDetails = async () => {
        try {
          const response = await bedev1Services.getGameDetails(gameData.placeId);
          setGame(response);
        } catch (e) {
          console.error(e);
        }
      };
      // eslint-disable-next-line no-void
      void fetchGameDetails();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div ref={ref} className='featured-grid-item-container game-card-container'>
        <Link
          onClick={() => {
            onTileClick(gameData, id);
          }}
          url={buildGameDetailUrl(gameData.placeId, gameData.name, {
            [queryParams.gameSearchQuerySession]: session
          })}
          className='game-card-link'
          id={gameData.universeId.toString()}>
          <Thumbnail2d
            type={ThumbnailTypes.gameIcon}
            size={ThumbnailGameIconSize.size512}
            targetId={gameData.universeId}
            containerClass='game-card-thumb-container'
            format={ThumbnailFormat.jpeg}
            altName={gameData.name}
          />
          <div className='game-card-name-info'>
            <div>
              <div className='game-card-name game-name-title' title={gameData.name}>
                {gameData.name}
              </div>
              <GameTileStats
                totalUpVotes={gameData.totalUpVotes}
                totalDownVotes={gameData.totalDownVotes}
                playerCount={gameData.playerCount}
              />
            </div>
            <PlayButton
              universeId={gameData.universeId}
              placeId={gameData.placeId}
              loadingComponent={LoadingPlayButton}
              purchaseComponent={PurchasePlayButton}
              errorComponent={ErrorPlayButton}
            />
          </div>
          {cardDescription}
        </Link>
      </div>
    );
  }
);

export default FeaturedGridTile;
